import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Database } from '../types/database'
import { DataLayer } from '../lib/dataLayer'
import { fixMasteryStatus } from '../utils/fixMasteryStatus'
import { sortCardsForStudy, filterCardsForStudy, LocalProgress, LevelSettings } from '../utils/cardSorting'
import { MasteryVisualization } from './MasteryVisualization'
import { StudyStartPage } from './StudyStartPage'
import { CardContent } from './study/CardContent'
import { useStudyKeyboard, commonStudyKeys } from '../hooks/useStudyKeyboard'
import { useTextToSpeech } from '../hooks/useTextToSpeech'
import { useCardActions, CardAction } from '../hooks/useCardActions'
import { getMaxCardsInRotation, getLevelSettings } from '../lib/storage'

type Card = Database['public']['Tables']['cards']['Row']

export interface ExerciseInfo {
  id: string
  name: string
}

interface StudyModeProps {
  persistentSessionStarted: boolean
  setPersistentSessionStarted: (value: boolean) => void
  persistentSelectedTags: string[]
  setPersistentSelectedTags: (value: string[]) => void
  persistentSelectedExercises: string[]
  setPersistentSelectedExercises: (value: string[]) => void
  shouldReset: boolean
  onResetComplete: () => void
  onAllCardsComplete: () => void
  onStartSequential?: (tags: string[], exercise: string) => void
  onExit?: () => void
  autoStart?: boolean
}

export function StudyMode({
  persistentSessionStarted: _persistentSessionStarted,
  setPersistentSessionStarted: _setPersistentSessionStarted,
  persistentSelectedTags,
  setPersistentSelectedTags,
  persistentSelectedExercises,
  setPersistentSelectedExercises,
  shouldReset,
  onResetComplete,
  onAllCardsComplete,
  onStartSequential,
  onExit,
  autoStart = false
}: StudyModeProps) {
  const { user } = useAuth()
  const [cards, setCards] = useState<Card[]>([])
  const [currentCardIndex, setCurrentCardIndex] = useState(0)
  const [showBack, setShowBack] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Study session state
  const [selectedTags, setSelectedTags] = useState<string[]>(persistentSelectedTags)
  const [availableTags, setAvailableTags] = useState<string[]>([])
  const [selectedExercises, setSelectedExercises] = useState<string[]>(persistentSelectedExercises)
  const [availableExercises, setAvailableExercises] = useState<ExerciseInfo[]>([])
  // Don't use persistentSessionStarted on mount - always start fresh
  const [sessionStarted, setSessionStarted] = useState(false)
  const [cardsInRotation, setCardsInRotation] = useState<Card[]>([])
  const [availableCards, setAvailableCards] = useState<Card[]>([])
  const [showTranslations, setShowTranslations] = useState(false)
  const [rotationSize, setRotationSize] = useState(30)
  const [cardProgress, setCardProgress] = useState<Map<string, LocalProgress>>(new Map())
  const [levelSettings, setLevelSettings] = useState<LevelSettings>({
    enabledLevels: ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'],
    filterMode: 'only'
  })

  // Card actions hook (handles progress updates, undo, action history)
  const {
    handleAction: processCardAction,
    undoLastAction,
    recordAction,
    clearHistory,
    canUndo
  } = useCardActions({
    userId: user?.id || '',
    onError: setError
  })

  // Load all cards and extract tags
  useEffect(() => {
    if (user) {
      // Run one-time fix for mastery status
      fixMasteryStatus(user.id).then(() => {
        loadCards()
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])
  
  // Auto-start when launched from exercise detail (skip the picker)
  useEffect(() => {
    if (autoStart && !sessionStarted && !loading && cards.length > 0) {
      startStudySession()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, loading, cards.length])
  
  // Load rotation size and level settings from storage
  useEffect(() => {
    setRotationSize(getMaxCardsInRotation())
    setLevelSettings(getLevelSettings())
  }, [])

  // Sync local state with persistent state (only tags and exercises)
  useEffect(() => {
    setSelectedTags(persistentSelectedTags)
  }, [persistentSelectedTags])

  useEffect(() => {
    setSelectedExercises(persistentSelectedExercises)
  }, [persistentSelectedExercises])

  useEffect(() => {
    setPersistentSelectedTags(selectedTags)
  }, [selectedTags, setPersistentSelectedTags])

  useEffect(() => {
    setPersistentSelectedExercises(selectedExercises)
  }, [selectedExercises, setPersistentSelectedExercises])

  // Handle reset when coming from completed cards
  useEffect(() => {
    if (shouldReset) {
      setSessionStarted(false)
      setSelectedTags([])
      setSelectedExercises([])
      setCardsInRotation([])
      setAvailableCards([])
      setCurrentCardIndex(0)
      setShowBack(false)
      clearHistory()
      onResetComplete()
    }
  }, [shouldReset, onResetComplete, clearHistory])

  const loadCards = async () => {
    try {
      setLoading(true)

      // Use DataLayer to get cards from local DB
      const cardsData = await DataLayer.getCards()

      // Get progress data from local DB
      const progressData = await DataLayer.getUserCardProgress(user!.id)

      // Filter out hidden and mastered cards
      const progressMap = new Map(
        progressData?.map(p => [p.card_id, p]) || []
      )

      const activeCards = (cardsData || []).filter(card => {
        const progress = progressMap.get(card.id)
        if (progress) {
          if (progress.status === 'mastered' || progress.status === 'hidden') {
            return false
          }
        }
        return true
      })

      setCards(activeCards)

      // Load exercises and build exercise info list
      const exercises = await DataLayer.getExercises()
      const exerciseMap = new Map(exercises.map(e => [e.id, e]))

      // Extract unique tags and exercise IDs from active cards
      const allTags = new Set<string>()
      const exerciseIds = new Set<string>()
      activeCards.forEach(card => {
        if (card.exercise_id) exerciseIds.add(card.exercise_id)
        card.tags.forEach((tag: string) => allTags.add(tag))
      })

      setAvailableTags(Array.from(allTags).sort())
      setAvailableExercises(
        Array.from(exerciseIds)
          .map(id => ({ id, name: exerciseMap.get(id)?.name || id }))
          .sort((a, b) => a.name.localeCompare(b.name))
      )
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load cards')
    } finally {
      setLoading(false)
    }
  }

  const startStudySession = async () => {
    // Clear action history when starting a new session
    clearHistory()

    // Filter cards based on selected exercises, tags, and level settings
    const filteredCards = filterCardsForStudy(cards, selectedExercises, selectedTags, levelSettings)

    if (filteredCards.length === 0) {
      setError('No cards match your selected tags')
      return
    }

    try {
      // Get progress data from local DB via DataLayer
      const allProgress = await DataLayer.getUserCardProgress(user!.id)

      // Filter to only cards we're studying and create a map
      const filteredCardIds = new Set(filteredCards.map(c => c.id))
      const progressMap = new Map<string, LocalProgress>(
        allProgress
          .filter(p => filteredCardIds.has(p.card_id))
          .map(p => [p.card_id, {
            mastery_score: p.mastery_score,
            status: p.status,
            last_reviewed: p.last_reviewed,
            review_count: p.review_count
          }])
      )

      // Sort cards and split into rotation and available
      const sortedCards = sortCardsForStudy(filteredCards, progressMap, levelSettings)
      const initialRotation = sortedCards.slice(0, Math.min(rotationSize, sortedCards.length))
      const remaining = sortedCards.slice(rotationSize)

      setCardsInRotation(initialRotation)
      setAvailableCards(remaining)
      setCardProgress(progressMap)
      setCurrentCardIndex(0)
      setSessionStarted(true)
      setShowBack(false)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    }
  }

  const handleCardAction = useCallback(async (action: CardAction) => {
    if (!user || cardsInRotation.length === 0) return

    const currentCard = cardsInRotation[currentCardIndex]

    // Save current state to history before making changes (for undo)
    const currentProgress = cardProgress.get(currentCard.id)
    recordAction(
      currentCard.id,
      action,
      currentProgress ? { ...currentProgress } : null,
      currentCardIndex,
      cardsInRotation,
      availableCards
    )

    // Process the action via hook
    const result = await processCardAction(currentCard.id, action)
    if (!result) return

    // Update local progress map
    const newProgressMap = new Map(cardProgress)
    newProgressMap.set(currentCard.id, {
      mastery_score: result.newMasteryScore,
      status: result.newStatus,
      last_reviewed: new Date().toISOString(),
      review_count: (currentProgress?.review_count || 0) + 1
    })
    setCardProgress(newProgressMap)

    // Handle rotation
    if (result.shouldRemoveFromRotation) {
      // Remove card from rotation
      let newRotation = cardsInRotation.filter((_, i) => i !== currentCardIndex)

      // Add a new card from available pool if we're below rotation size
      if (newRotation.length < rotationSize && availableCards.length > 0) {
        // Take the first available card (which is the highest priority due to our sorting)
        const newCard = availableCards[0]
        const newAvailable = availableCards.slice(1)

        // Add to rotation at a random position
        const insertIndex = Math.floor(Math.random() * (newRotation.length + 1))
        newRotation = [
          ...newRotation.slice(0, insertIndex),
          newCard,
          ...newRotation.slice(insertIndex)
        ]

        setAvailableCards(newAvailable)
      }

      if (newRotation.length === 0 && availableCards.length === 0) {
        // All cards completed!
        setSessionStarted(false)
        alert('Amazing! You\'ve mastered all available cards!')
        onAllCardsComplete()
      } else if (newRotation.length === 0) {
        // Rotation empty but cards available - shouldn't happen but just in case
        const refillCount = Math.min(rotationSize, availableCards.length)
        newRotation = availableCards.slice(0, refillCount)
        setAvailableCards(availableCards.slice(refillCount))
      }

      setCardsInRotation(newRotation)
      setCurrentCardIndex(Math.min(currentCardIndex, newRotation.length - 1))
    } else {
      // Move to next card
      setCurrentCardIndex((currentCardIndex + 1) % cardsInRotation.length)
    }

    setShowBack(false)
    setShowTranslations(false)
  }, [user, cardsInRotation, currentCardIndex, availableCards, rotationSize, cardProgress, onAllCardsComplete, recordAction, processCardAction])

  const handleUndo = useCallback(async () => {
    if (!canUndo) return

    const lastAction = await undoLastAction()
    if (!lastAction) return

    // Restore local state
    setCardsInRotation(lastAction.previousCardsInRotation)
    setAvailableCards(lastAction.previousAvailableCards)
    setCurrentCardIndex(lastAction.previousCardIndex)

    // Restore progress map
    const newProgressMap = new Map(cardProgress)
    if (lastAction.previousProgress) {
      newProgressMap.set(lastAction.cardId, {
        mastery_score: lastAction.previousProgress.mastery_score ?? 0,
        status: lastAction.previousProgress.status ?? 'active',
        last_reviewed: lastAction.previousProgress.last_reviewed ?? null,
        review_count: lastAction.previousProgress.review_count ?? 0
      })
    } else {
      newProgressMap.delete(lastAction.cardId)
    }
    setCardProgress(newProgressMap)

    // Reset card display state
    setShowBack(false)
    setShowTranslations(false)
  }, [canUndo, undoLastAction, cardProgress])

  // Keyboard shortcuts
  useStudyKeyboard({
    enabled: sessionStarted,
    showBack,
    keyMappings: [
      commonStudyKeys.showAnswer(setShowBack, showBack),
      { keys: ['a'], handler: () => handleCardAction('mastered'), requiresShowBack: true },
      { keys: ['s'], handler: () => handleCardAction('recalled'), requiresShowBack: true },
      { keys: ['d'], handler: () => handleCardAction('missed'), requiresShowBack: true },
      { keys: ['h'], handler: () => handleCardAction('hide'), requiresShowBack: true },
      commonStudyKeys.toggleTranslations(showTranslations, setShowTranslations),
      commonStudyKeys.speakAll(),
      commonStudyKeys.toggleAudio(),
      { keys: ['z'], handler: () => { handleUndo() } },
    ],
  })

  // Set up text-to-speech with current card's language
  const currentCardForTTS = cardsInRotation[currentCardIndex]
  useTextToSpeech({
    language: currentCardForTTS?.language || 'es',
    enabled: sessionStarted && !!currentCardForTTS
  })

  if (loading) {
    return <div className="text-center py-4">Loading cards...</div>
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <p className="text-sm text-red-800">{error}</p>
      </div>
    )
  }
  
  const handleExerciseToggle = (exercise: string) => {
    if (selectedExercises.includes(exercise)) {
      setSelectedExercises(selectedExercises.filter(e => e !== exercise))
    } else {
      setSelectedExercises([...selectedExercises, exercise])
    }
  }

  const handleTagToggle = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag))
    } else {
      setSelectedTags([...selectedTags, tag])
    }
  }

  if (!sessionStarted) {
    return (
      <StudyStartPage
        cards={cards}
        availableExercises={availableExercises}
        availableTags={availableTags}
        selectedExercises={selectedExercises}
        selectedTags={selectedTags}
        onExerciseToggle={handleExerciseToggle}
        onTagToggle={handleTagToggle}
        onStartStudy={startStudySession}
        onStartSequential={onStartSequential}
        levelSettings={levelSettings}
      />
    )
  }

  const currentCard = cardsInRotation[currentCardIndex]
  if (!currentCard) {
    return null
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          {onExit && (
            <button
              onClick={onExit}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
              Exit
            </button>
          )}
          <div className="text-sm text-gray-500">
            <span>In rotation: {cardsInRotation.length}</span>
            {availableCards.length > 0 && (
              <span className="ml-3">In reserve: {availableCards.length}</span>
            )}
          </div>
        </div>
        <button
          onClick={() => {
            setSessionStarted(false)
            setSelectedTags([])
            setSelectedExercises([])
          }}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Select Cards
        </button>
      </div>
      
      <div className="bg-white shadow rounded-lg p-6">
        <div className="space-y-6">
          {/* Mastery Progress Indicator */}
          {user && (
            <MasteryVisualization
              userId={user.id}
              cardId={currentCard.id}
              currentMastery={cardProgress.get(currentCard.id)?.mastery_score || 0}
            />
          )}
          
          <div>
            <CardContent
              content={currentCard.front}
              showTranslations={showTranslations}
              onToggleTranslations={() => setShowTranslations(!showTranslations)}
            />
          </div>

          {showBack && (
            <div className="border-t pt-6">
              <CardContent
                content={currentCard.back}
                showTranslations={showTranslations}
                onToggleTranslations={() => setShowTranslations(!showTranslations)}
              />
            </div>
          )}

          <div className="flex justify-center space-x-4 pt-4">
            {!showBack ? (
              <button
                onClick={() => setShowBack(true)}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Show Answer
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleCardAction('mastered')}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                >
                  Mastered
                </button>
                <button
                  onClick={() => handleCardAction('recalled')}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Recalled
                </button>
                <button
                  onClick={() => handleCardAction('missed')}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700"
                >
                  Missed
                </button>
                <button
                  onClick={() => handleCardAction('hide')}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700"
                >
                  Hide
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Keyboard shortcuts help - hidden on mobile */}
      <div className="mt-4 text-center hidden sm:block">
        <details className="inline-block">
          <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
            Keyboard shortcuts
          </summary>
          <div className="mt-2 bg-white rounded-lg shadow p-4 text-left text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Space</kbd> Show answer</div>
              <div><kbd className="px-2 py-1 bg-gray-100 rounded text-xs">A</kbd> Mastered</div>
              <div><kbd className="px-2 py-1 bg-gray-100 rounded text-xs">S</kbd> Recalled</div>
              <div><kbd className="px-2 py-1 bg-gray-100 rounded text-xs">D</kbd> Missed</div>
              <div><kbd className="px-2 py-1 bg-gray-100 rounded text-xs">H</kbd> Hide</div>
              <div><kbd className="px-2 py-1 bg-gray-100 rounded text-xs">T</kbd> Toggle translations</div>
              <div><kbd className="px-2 py-1 bg-gray-100 rounded text-xs">F</kbd> Speak all text</div>
            </div>
          </div>
        </details>
      </div>
    </div>
  )
}